import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../services/api';
import websocketService from '../services/websocketService';

const usePermissionSync = (userAccountType) => {
  const [permissions, setPermissions] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // WebSocket连接状态
  const intervalRef = useRef(null);
  const isCheckingRef = useRef(false);

  // 检查权限更新
  const checkPermissionUpdate = useCallback(async () => {
    // 只有RAM账号才需要检查权限更新
    if (userAccountType !== 'ram' || isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      const response = await API.get('/api/auth/check-permission-update');
      const { permissionsUpdatedAt, permissions: newPermissions } = response.data;

      // 如果是第一次获取或者权限有更新
      if (!lastUpdateTime || new Date(permissionsUpdatedAt) > new Date(lastUpdateTime)) {
        setPermissions(newPermissions);
        setLastUpdateTime(permissionsUpdatedAt);
        
        // 触发权限更新事件（静默）
        window.dispatchEvent(new CustomEvent('permissionsUpdated', {
          detail: { 
            permissions: newPermissions,
            source: 'polling'
          }
        }));
      }
    } catch (error) {
      console.error('检查权限更新失败:', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [userAccountType, lastUpdateTime]);

  // 手动刷新权限
  const refreshPermissions = useCallback(() => {
    setLastUpdateTime(null); // 强制刷新
    checkPermissionUpdate();
  }, [checkPermissionUpdate]);

  // 启动定时检查（只对RAM账号）
  const startPolling = useCallback((interval = 30000) => { // 默认30秒检查一次
    if (userAccountType !== 'ram') return;

    // 立即检查一次
    checkPermissionUpdate();

    // 设置定时器
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(checkPermissionUpdate, interval);
  }, [userAccountType, checkPermissionUpdate]);

  // 停止定时检查
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // WebSocket连接管理（支持所有账号类型）
  const connectWebSocket = useCallback(() => {
    if (userAccountType) {
      // 检查WebSocket是否可用
      if (!websocketService.isAvailable()) {
        console.log('WebSocket不可用，仅使用定时轮询进行权限同步');
        return;
      }

      // 为所有账号类型启动WebSocket连接（用于单点登录和会话管理）
      websocketService.connect(userAccountType);
      websocketService.startHeartbeat();
      
      // 监听WebSocket连接状态变化
      const checkConnectionStatus = () => {
        const status = websocketService.getConnectionState().toLowerCase();
        setConnectionStatus(status);
      };
      
      // 定期检查连接状态
      const statusInterval = setInterval(checkConnectionStatus, 5000); // 增加到5秒
      
      return () => {
        clearInterval(statusInterval);
      };
    }
  }, [userAccountType]);

  const disconnectWebSocket = useCallback(() => {
    // 静默断开WebSocket连接
    websocketService.disconnect();
    setConnectionStatus('disconnected');
  }, []);

  // 监听WebSocket权限更新消息（只对RAM账号有效）
  useEffect(() => {
    const handleWebSocketPermissionUpdate = (event) => {
      const { permissions, source } = event.detail;
      
      if (source === 'websocket' && userAccountType === 'ram') {
        // 静默更新权限（只对RAM账号）
        setPermissions(permissions);
        setLastUpdateTime(new Date().toISOString());
      }
    };

    window.addEventListener('permissionsUpdated', handleWebSocketPermissionUpdate);
    
    return () => {
      window.removeEventListener('permissionsUpdated', handleWebSocketPermissionUpdate);
    };
  }, [userAccountType]);

  // 组件挂载时启动，卸载时清理
  useEffect(() => {
    // 启动WebSocket连接（所有账号类型）
    const cleanup = connectWebSocket();
    
    // 只对RAM账号启动定时轮询
    if (userAccountType === 'ram') {
      startPolling();
    }
    
    return () => {
      if (userAccountType === 'ram') {
        stopPolling();
      }
      disconnectWebSocket();
      if (cleanup) cleanup();
    };
  }, [userAccountType, startPolling, stopPolling, connectWebSocket, disconnectWebSocket]);

  // 页面焦点变化时的处理
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 页面隐藏时，只对RAM账号停止轮询，但保持WebSocket连接
        if (userAccountType === 'ram') {
          stopPolling();
        }
      } else {
        // 页面显示时恢复轮询并立即检查（只对RAM账号）
        if (userAccountType === 'ram') {
          startPolling();
        }
        // 如果WebSocket可用但未连接，尝试重连（所有账号类型）
        if (websocketService.isAvailable() && !websocketService.isConnected()) {
          connectWebSocket();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userAccountType, startPolling, stopPolling, connectWebSocket]);

  return {
    permissions,
    lastUpdateTime,
    connectionStatus, // WebSocket连接状态
    refreshPermissions,
    startPolling,
    stopPolling,
    checkPermissionUpdate,
    connectWebSocket,
    disconnectWebSocket
  };
};

export default usePermissionSync; 